/* ⚠ 2026-10-02 eric — TC Rack · 팁 랙 그림으로 바꿈. 되돌릴 때 이 블록을 살린다: import type { CSSProperties } from 'react' */
import type { FreeFormObject } from '@ctcells/contract'
import { t } from '@ctcells/i18n'
import type { CSSProperties, ReactNode } from 'react'

import { cx } from '../../atoms/cx.ts'
import { DeckRackFigure, type DeckRackKind } from '../../figures/DeckRackFigure.tsx'
import { bundledDefinition } from '../../figures/deckRackDefinitions.ts'
import { opentronsLayout, type DefinitionLayout } from '../../figures/opentronsLayout.ts'
import { DECK_PLATE_LONG, TC_PLATE_H, TC_PLATE_W } from '../../figures/rackPlateGeometry.ts'
/*
 * ⚠ 2026-10-02 eric — TC Rack · 팁 랙 그림으로 바꿈. 되돌릴 때 이 블록을 살린다(위 import 에 `type WellFill` 도 같이):
 * import { REST_CELLS } from '../../figures/deckRackDefinitions.ts'
 */

import {
  rackAxes,
  rackOccupancy,
  thinAxisLabels,
  wellPresence,
  type ReagentBay,
  type ReagentPresence,
  type ReagentSupply,
  type SupplyItem,
  type SupplySummary,
} from './supplySummary.ts'
import { reagentBayName } from './supplyText.ts'
import { useBoxHeightVar } from './useBoxMeasure.ts'

import '../screens.css'
import './supply.css'

/**
 * **소모품 칸 격자 — 팁 랙 · TC 적재소(`SlotGrid`) · 시약 줄 목록(`ReagentList`)**. 모니터링 오른쪽 소모품 판(`SupplyPanel`)이 부른다.
 *
 * ⚠ 이 파일은 종전 **소모품 상세 팝오버의 내용**(`SupplyDetail` — 머리 · 수치 타일 셋 · 칸 격자 · 범례 · 사실 줄 · 최근 분주 기록 ·
 *   「보충」)이었다. eric 2026-10-02 「시약이나 소모품 클릭하면 팝업이 나오는데.. 안 나오게 해줘」로 팝오버를 걷으면서 판이 다시 쓰는
 *   칸 격자 둘만 남겼다. 보충은 판 머리의 「보충」 버튼(`SupplyReplenishButton`)이 확인 창을 바로 연다. 되살리지 마라.
 *
 * 🔴 **값을 새로 셈하지 않는다.** 요약 카드와 같은 `supplySummary()` 결과를 받는다.
 * 🔴 **칸 상태는 `rackOccupancy()` 가 정한다**(= `deckOccupancy` · eric 확정 2026-09-28 「수로 칸을 고르지 마라」의 예외 둘 —
 *    0 이면 전부 비움 · 가득이면 전부 가득). 팁 랙은 장비가 칸으로 보고한 쓴 칸(`tips_by_rack` → 레시피 `well_names` ·
 *    `SupplyItem.usedWells`)이 있으면 그 칸만 빈 원이다. 그것도 없는 «사이» 값은 어느 칸이 비었는지 모르므로 격자는 가득으로 두고
 *    그 아래에 「칸별 사용 기록 미수신 — 그림은 가득으로 표시」를 적는다(수로 칸을 고르지 않는다).
 * 🔴 **시약은 시약 하나당 있다/없다**(eric 2026-09-28 「채웠냐. 안채웠냐만 알면 돼」 → 2026-10-02 「시약은 있냐 없냐로만」) —
 *    시약마다 있음 · 없음 · 미수신 세 얼굴의 한 줄이다(`ReagentList`). 웰 격자가 아니다.
 */

/*
 * ⚠ 2026-10-02 eric — TC Rack 그림으로 바꿈 · 팁 랙 그림으로 바꿈. 되돌릴 때 이 블록을 살린다.
 *   (eric 「1,2,3,4,5,6,7,8,9 를 원래 TC rack 모양으로 수정해줘 … 지금 것은 주석 처리하고」 · 「이거 팁랙도.. 그림으로 수정해줘봐봐」)
 *   아래는 바꾸기 전의 `SlotGrid` 원문(TC 번호 네모 3 × 3 · 팁 세로 원 격자 8 × 12)이다. 살릴 때는 이 블록 안의 역슬래시 든 닫는 표지(`*\/`)에서 역슬래시를 빼고
 *   주석을 풀고, 그 밑의 새 `SlotGrid` 를 걷고, 맨 위 import 의 같은 날짜 주석(`CSSProperties` · `WellFill` · `REST_CELLS`)과
 *   `supplyPanel.css` · `supply.css` 의 같은 날짜 주석 블록을 살린다. 시험은 `supplyChip.test.tsx` 의 `it.skip` 둘.
 *
 * /** 칸 격자의 열 수 — TC 적재소 3(시안). 팁 랙은 랙 행 수(A~H = 8 · 세로 랙)가 열 수다(아래 `tipCellName`) *\/
 * const GRID_COLUMNS: Record<'tcstock', number> = {
 *   tcstock: 3,
 * }
 *
 * /** 팁 랙 한 줄(1~12)의 칸 수 — 96 구 랙(A~H × 1~12)의 번호 쪽. 행 수(A~H)는 칸 수 ÷ 이 값이다 *\/
 * const TIP_RACK_NUMBERS = 12
 *
 * /**
 *  * **세로 팁 랙의 칸 이름**(eric 2026-10-02 「팁랙이 가로로 되어 있는데.. 세로로 해줘」) — 그린 차례(위 → 아래 · 왼쪽 → 오른쪽)의
 *  * `i` 번째 칸이 어느 웰인가.
 *  * 🔴 **데크 그림과 같은 방향이다** — 표준 방향(A1 왼쪽 위 · 1~12 가로)을 **시계 방향 90°** 돌린 모양(`DeckRackFigure` 의
 *  *    `opentronsLayout(def, { rotate: 90 })` · 사진 `docs/stl/…_02.jpg` 의 12 행 × 8 열). ⇒ **A1 오른쪽 위** · 맨 위 줄이 1번
 *  *    (왼쪽 → 오른쪽 H1 … A1) · 맨 아래 줄이 12번(H12 … A12) · A 열이 오른쪽 끝이다.
 *  * 🔴 칸 상태는 이 이름으로 찾는다(`rackOccupancy` · 쓴 칸 = `well_names[N-1]`) — 자리를 돌려도 이름 ↔ 상태는 안 바뀐다.
 *  *    좌우를 뒤집으면(A1 왼쪽 위 · 전치) 실물에 없는 거울 모양이 된다. 되돌리지 마라.
 *  *\/
 * function tipCellName(i: number, letters: number): string {
 *   const row = Math.floor(i / letters)
 *   const col = i % letters
 *   return `${String.fromCharCode(65 + (letters - 1 - col))}${String(row + 1)}`
 * }
 *
 * /**
 *  * 팁 · TC 칸 격자 — 칸 이름은 팁 A1~H12(세로 랙 · 열 H~A · 줄 1~12 · `tipCellName`) · TC 는 1~9. 상태는 `rackOccupancy()`(= `deckOccupancy`) 그대로다.
 *  * 🔴 보고된 수가 0 과 전체 «사이»면 — 장비가 칸으로 보고했으면(`usedWells`) 그 칸만 비고, 아니면 어느 칸인지 모른다
 *  *    (격자는 가득이고 그 사실을 한 줄로 적는다).
 *  * 🔴 시료별 보기 오른쪽 소모품 판(`SupplyPanel`)이 이 격자를 부른다(2026-10-01) — 두 벌로 만들지 마라.
 *  *\/
 * export function SlotGrid({ item, summary }: { item: SupplyItem; summary: SupplySummary }) {
 *   const rack = item.racks[0]
 *   if (rack === undefined) return null
 *   const tips = rack.kind !== 'tcstock'
 *   /* 팁 랙은 세로 — 열 = 랙 행(A~H) 수 · 줄 = 번호(1~12) *\/
 *   const cols = tips ? Math.max(1, Math.round(item.total / TIP_RACK_NUMBERS)) : GRID_COLUMNS.tcstock
 *   const occupancy = rackOccupancy(summary, rack)
 *   const cells = Array.from({ length: item.total }, (_u, i) => {
 *     const name = tips ? tipCellName(i, cols) : String(i + 1)
 *     const fill: WellFill = occupancy[name] ?? occupancy[REST_CELLS] ?? 'full'
 *     return { name, fill }
 *   })
 *   const note =
 *     item.mismatch === true
 *       ? t('monitoring.supply.tipsMismatchNote')
 *       : item.unit === 'count' && item.remaining === null
 *         ? t('monitoring.supply.rackSplitUnknown')
 *         : /* 칸을 못 풀어 격자가 가득으로 서는 경우의 「칸별 사용 기록 미수신」 줄은 걷었다(eric 2026-10-02) — 수치와 격자만 *\/
 *           undefined
 *   return (
 *     <>
 *       <div
 *         aria-label={t('monitoring.supply.wells')}
 *         className={cx('supdetail__grid', tips ? 'supdetail__grid--tips' : 'supdetail__grid--slots')}
 *         role="img"
 *         style={{ '--c': String(cols) } as CSSProperties}
 *       >
 *         {cells.map((cell) => (
 *           <i
 *             className={cx(
 *               'supdetail__cell',
 *               tips ? 'supdetail__cell--tip' : 'supdetail__cell--sq',
 *               cell.fill === 'full' && 'is-on',
 *             )}
 *             data-fill={cell.fill}
 *             data-well={cell.name}
 *             key={cell.name}
 *             title={cell.name}
 *           >
 *             {tips ? null : <span>{cell.name}</span>}
 *           </i>
 *         ))}
 *       </div>
 *       {note === undefined ? null : <p className="supdetail__note">{note}</p>}
 *     </>
 *   )
 * }
 */

/**
 * 칸 그림 아래 사유 한 줄 — 칸별 기록과 합계 불일치 · 랙별로 못 나눔. 그 밖에는 없다
 * (칸을 못 풀어 그림이 가득으로 서는 경우의 「칸별 사용 기록 미수신」 줄은 걷었다 · eric 2026-10-02 — 수치와 그림만).
 */
export function slotGridNote(item: SupplyItem): string | undefined {
  if (item.mismatch === true) return t('monitoring.supply.tipsMismatchNote')
  if (item.unit === 'count' && item.remaining === null) {
    return t('monitoring.supply.rackSplitUnknown')
  }
  return undefined
}

/**
 * **팁 랙 · TC 칸 그림 — 장비 탭의 랙 그림 그대로**(`DeckRackFigure` · eric 2026-10-02 「원래 TC rack 모양으로」 · 「팁랙도.. 그림으로」).
 * 🔴 **새 그림을 만들지 않는다** — 장비 탭(`FocusFigure` 의 `realFigures` 길 · `deckRacks(…)`)이 그리는 그 컴포넌트에 같은 `kind` 를 넘긴다.
 *    정의는 도는 작업 레시피의 그 랙 정의가 먼저다(`SupplyItem.definition` — 칸 수 `total` 과 같은 출처) · 없으면 그림이 번들 사본
 *    (Opentrons 96 팁 랙 · TC 는 우리 전용 9칸 · 장비 탭과 같다). 방향도 그 그림의 것이다 — 팁 랙은 표준 방향을
 *    시계 방향 90° 돌린 세로(A1 오른쪽 위 · H12 왼쪽 아래 · `opentronsLayout(def, { rotate: 90 })`), TC 는 가로 판 3 × 3.
 * 🔴 칸 상태는 `rackOccupancy()`(= `deckOccupancy`) 한 곳이다 — 장비 탭과 같은 함수 · 같은 열쇠(팁 = 웰 이름 · TC = '1'…'9').
 *    장비가 칸으로 보고한 쓴 칸(`usedWells` ⇔ `well_names[N-1]`)만 비고, 0 이면 전부 빈 칸 · 가득이면 전부 찬 칸 · 그 밖의 «사이»는
 *    어느 칸인지 모르므로 가득으로 둔다(수로 칸을 고르지 않는다) — 단 TC 는 A1 → C3 차례 앞에서부터 비운다(`tcUsedCells` · eric 2026-10-29).
 * 🔴 색만으로 말하지 않는다 — 찬 칸은 채움 노드(팁 머리 · TC 판)가 있고 쓴 칸은 빈 구멍 테만 남는다(모양이 말한다) · 머리 수치가 같은 사실을 말한다.
 * 🔴 시료별 보기 오른쪽 소모품 판(`SupplyPanel`)이 이 함수를 부른다 — 두 벌로 만들지 마라.
 * `bare` 면 그림 상자만 그린다 — 사유 한 줄(`slotGridNote()`)은 부르는 쪽이 제 자리에 둔다(소모품 판 6칸 · 오른쪽 «구성» 목록 아래 · 2026-10-06).
 */
export function SlotGrid({
  item,
  summary,
  bare = false,
}: {
  item: SupplyItem
  summary: SupplySummary
  bare?: boolean
}) {
  /* 그림 상자 키(`--sp-fig-h`) — 소모품 판의 축 라벨 틀이 그림 키로 읽는다(`supplyPanel.css` · `100cqh` 대신) */
  const figRef = useBoxHeightVar<HTMLDivElement>('--sp-fig-h')
  const rack = item.racks[0]
  if (rack === undefined) return null
  const occupancy = rackOccupancy(summary, rack)
  const note = bare ? undefined : slotGridNote(item)
  return (
    <>
      <div
        ref={figRef}
        className={cx(
          'supdetail__grid supdetail__grid--fig',
          rack.kind === 'tcstock' ? 'supdetail__grid--tcrack' : 'supdetail__grid--tiprack',
        )}
      >
        <RackAxesFrame definition={item.definition} kind={rack.kind}>
          <DeckRackFigure
            definition={item.definition}
            kind={rack.kind}
            label={t('monitoring.supply.wells')}
            occupancy={occupancy}
          />
        </RackAxesFrame>
      </div>
      {note === undefined ? null : <p className="supdetail__note">{note}</p>}
    </>
  )
}

/**
 * 랙 그림이 그리는 칸 배치 — `DeckRackFigure` 와 **같은 정의 · 같은 계산**(`opentronsLayout` · 시계 방향 90° · 긴 변
 * `DECK_PLATE_LONG`). 레시피 정의로 그릴 수 있으면 그것, 아니면 번들 사본(그림의 `resolveRack` 차례 그대로). TC 는 칸 이름이 번호뿐이라 없다.
 * 🔴 그림 쪽 계산을 바꾸면 여기도 같이 — `supplyChip.test.tsx` 가 라벨 자리를 그려진 구멍 좌표와 대조한다(어긋나면 걸린다).
 */
function rackLayoutOf(
  kind: DeckRackKind,
  definition: FreeFormObject | undefined,
): DefinitionLayout | null {
  if (kind === 'tcstock') return null
  /*
   * 🔴 그림(`DeckRackFigure`)과 같은 방향 — **세로(시계 방향 90°)**. eric 2026-10-13 「어제 소모품들.. 내가 가로로 바꾸라고 했는데.. 다시
   *    세로로 해줘」 — 10-12 의 Opentrons 표준 가로(`standard` · `96bfacc`)를 되돌렸다. 축 글자는 칸 이름의 실제 좌표에서 잰다(`rackAxes`) —
   *    세로 판은 글자가 머리 줄(A 가 오른쪽 · 시계 방향으로 돈 행) · 숫자가 왼쪽 줄이라 이름표가 칸과 어긋나지 않는다.
   */
  const options = { rotate: 90, fitLong: DECK_PLATE_LONG } as const
  const fromRecipe = definition === undefined ? null : opentronsLayout(definition, options)
  if (fromRecipe !== null) return fromRecipe
  const bundled = bundledDefinition(kind)
  return bundled === undefined ? null : opentronsLayout(bundled, options)
}

/**
 * TC rack 축 라벨 — 라벨마다 그 줄에 드는 서버 칸 번호(그림 `data-cell`). **글자가 행**이다 — 왼쪽 줄 A · B · C(위 → 아래) · 머리 줄 1 · 2 · 3
 * (왼쪽 → 오른쪽)(eric 2026-10-12 「보통 A, B, C 가 행이야? 행이면 A, B, C 가 위에서 아래로」). 종전(머리 C · B · A · 왼쪽 1 · 2 · 3)은
 * 팁 랙의 돌린 방향(A 열이 오른쪽)을 흉내 낸 것이었다 — 🔴 TC 칸에는 서버가 준 글자 이름이 없다(`tc_stock` 1~9 번호뿐). 글자는 우리가 단 표지다.
 * 대응 규칙은 `RackAxesFrame` 머리말. 자리(`--at`)는 그려진 칸 중심의 평균이다.
 */
const TC_AXES = {
  head: [
    { label: '1', cells: ['7', '8', '9'] },
    { label: '2', cells: ['4', '5', '6'] },
    { label: '3', cells: ['1', '2', '3'] },
  ],
  side: [
    { label: 'A', cells: ['1', '4', '7'] },
    { label: 'B', cells: ['2', '5', '8'] },
    { label: 'C', cells: ['3', '6', '9'] },
  ],
} as const

/**
 * 라벨 자리를 **그려진 칸**에서 읽어 단다(TC rack) — 틀이 붙을 때 한 번. 그림의 `viewBox` 와 칸 구멍(`.fig-mod__hole` 사각)의 중심으로
 * 라벨마다 `data-axis-cells` 칸들의 평균 자리를 그림 폭 · 높이에 대한 비율로 `--at` 에 적는다(상태가 아니라 DOM 스타일이라 다시 그리지 않는다).
 */
function placeFromFigure(frame: HTMLDivElement | null) {
  if (frame === null) return
  const svg = frame.querySelector('svg.fig-deckrack')
  const box = (svg?.getAttribute('viewBox') ?? '').split(' ').map(Number)
  const vw = box[2]
  const vh = box[3]
  if (vw === undefined || vh === undefined || !(vw > 0) || !(vh > 0)) return
  const centers = new Map<string, { x: number; y: number }>()
  for (const cell of frame.querySelectorAll<SVGGElement>('[data-cell]')) {
    const hole = cell.querySelector('.fig-mod__hole')
    const key = cell.getAttribute('data-cell')
    if (hole === null || key === null) continue
    const num = (name: string) => Number(hole.getAttribute(name))
    centers.set(
      key,
      hole.tagName.toLowerCase() === 'rect'
        ? { x: num('x') + num('width') / 2, y: num('y') + num('height') / 2 }
        : { x: num('cx'), y: num('cy') },
    )
  }
  for (const label of frame.querySelectorAll<HTMLElement>('[data-axis-cells]')) {
    const points = (label.dataset['axisCells'] ?? '')
      .split(',')
      .map((key) => centers.get(key))
      .filter((p) => p !== undefined)
    if (points.length === 0) continue
    const horizontal = label.dataset['axis'] === 'x'
    const mean = points.reduce((sum, p) => sum + (horizontal ? p.x : p.y), 0) / points.length
    label.style.setProperty('--at', String(mean / (horizontal ? vw : vh)))
  }
}

/**
 * 이웃 축 글자 사이 최소 틈 — 글자 높이에 대한 비. 0 = 글자 상자끼리 맞닿음까지 허용한다: 상자(줄 높이 1)에 이미 글리프 위아래 여백이 있어
 * 맞닿아도 글자는 안 포갠다(냉장 24구 1~6 · 팁 랙 머리 H~A 가 그 간격으로 읽힌다). 팁 랙 왼쪽 1~12 는 칸 간격(≈ 9px)이 줄 높이(12)보다 좁아 솎인다
 */
const AXIS_LABEL_GAP = 0

/**
 * 축 글자 솎기를 «그려진 크기»로 — 머리 줄은 그림 폭, 왼쪽 줄은 그림 높이에서 글자 자리(`--at`)를 화면 px 로 되돌려 이웃 간격을 잰다
 * (`thinAxisLabels`). 솎인 글자는 `data-axis-thinned` 로 숨긴다(자리 · DOM 은 그대로 — 라벨은 보조기술에 숨긴 장식이다).
 */
function thinAxes(frame: HTMLElement) {
  for (const [selector, horizontal] of [
    ['.rackaxes__head', true],
    ['.rackaxes__side', false],
  ] as const) {
    const box = frame.querySelector<HTMLElement>(selector)
    if (box === null) continue
    const rect = box.getBoundingClientRect()
    const span = horizontal ? rect.width : rect.height
    if (!(span > 0)) continue
    const labels = [...box.querySelectorAll<HTMLElement>(':scope > .rackaxes__label')]
    const sized = labels.map((el) => {
      const r = el.getBoundingClientRect()
      return {
        label: el.textContent ?? '',
        pos: Number(el.style.getPropertyValue('--at')) * span,
        size: horizontal ? r.width : r.height,
        line: r.height,
      }
    })
    const keep = thinAxisLabels(sized, Math.max(0, ...sized.map((s) => s.line)) * AXIS_LABEL_GAP)
    labels.forEach((el, i) => el.toggleAttribute('data-axis-thinned', keep[i] === false))
  }
}

/** 틀이 붙으면 그림 칸 크기가 바뀔 때마다 다시 솎는다(카드 폭 · 창 크기 · 모달) — React 19 ref 정리 함수로 끊는다 */
function thinOnResize(frame: HTMLDivElement | null) {
  if (frame === null || typeof ResizeObserver === 'undefined') return
  const fig = frame.querySelector('.rackaxes__fig')
  if (fig === null) return
  const observer = new ResizeObserver(() => thinAxes(frame))
  observer.observe(fig)
  return () => observer.disconnect()
}

/**
 * **랙 그림 둘레의 축 라벨 — 엑셀 표처럼 머리 줄 글자 · 왼쪽 숫자**(eric 2026-10-02 「오른쪽 소모품에서.. 사용자가 바로 알기 쉽게..
 * A, B, C 이거.. 엑셀의 테이블처럼.. 헤드와.. 왼쪽에.. 숫자를 적어줄래?」).
 *
 * ```
 *     H G F E D C B A      ← 머리 줄 = 칸 이름의 글자(세로 판이라 A 가 오른쪽 끝 · A1 오른쪽 위)
 *  1 ┌───────────────┐
 *  2 │  랙 그림       │     ← 왼쪽 줄 = 칸 이름의 숫자(1 이 맨 위)
 *  … └───────────────┘
 * ```
 * 🔴 **라벨은 실제 칸 이름이다** — 글자 · 숫자와 자리는 그림과 같은 칸 배치(`rackLayoutOf`)의 칸 이름 · 중심에서 온다(`rackAxes()`).
 *    eric 예시(머리 A, B, C · 왼쪽 1…9)와 순서가 달라도(머리가 H … A) 칸 이름이 이긴다 — 라벨이 칸 이름과 어긋나면 더 헷갈린다.
 * 🔴 **TC rack 은 3 × 3 라벨을 단다**(eric 2026-10-02 「왜 tc rack 은 숫자 안 붙여? A, B, C 1, 2, 3」 — 처음엔 칸 이름이 번호뿐이라 안 달았다).
 *    서버 칸 번호(`tc_stock` 1~9 · 그림의 `data-cell`)에 표지를 단다(`TC_AXES`) — 번호는 그림처럼 오른쪽 열 위에서 아래로 1 · 2 · 3,
 *    그 왼쪽 열이 4 · 5 · 6, 맨 왼쪽 열이 7 · 8 · 9 다. ⇒ **행 글자 A · B · C(위 → 아래) · 열 숫자 1 · 2 · 3(왼쪽 → 오른쪽)**(eric 2026-10-12
 *    「A, B, C 가 행이면 위에서 아래로」 — 종전 「열 글자 오른쪽부터 A」는 팁 랙 흉내였다). 칸 k = 행 `ABC[(k−1) mod 3]` + 열 `3 − ⌊(k−1)/3⌋`.
 *    TC 칸 자리 계산은 그림 안에만 있어(공개 함수가 아니다) 자리는 그려진 칸에서 읽는다(`placeFromFigure`).
 * 🔴 `DeckRackFigure` 는 손대지 않는다(장비 관점 앱도 쓴다) — 그림 «밖»에 줄을 두르고, 자리는 그림 폭 · 높이에 대한 비율이라 그림 크기가
 *    바뀌어도 칸과 맞는다. 라벨은 보조기술에 숨긴다(그림의 접근 이름 · 줄 목록의 칸 이름이 말한다).
 * 10구 튜브 랙처럼 같은 글자가 묶음마다 다른 자리에 서면 둘째 묶음의 글자는 한 줄 더 선다(`bands`) — 마지막 묶음(판 아래 끝까지)은 랙 «밖» 아래
 *    줄(`--foot`)이다(2026-10-26 · 판 위 틈에 세우면 큰 튜브 웰을 덮었다). 칸이 촘촘해 글자가 포개지면 k 개마다 하나만 남긴다(`thinAxes`).
 */
export function RackAxesFrame({
  kind,
  definition,
  children,
}: {
  kind: DeckRackKind
  definition: FreeFormObject | undefined
  children: ReactNode
}) {
  if (kind === 'tcstock') {
    return (
      <div
        className="rackaxes"
        data-rack={kind}
        ref={placeFromFigure}
        /* TC 판은 가로로 긴 판 — 그림과 같은 판 상수(`TC_PLATE_W × TC_PLATE_H` · 2026-10-19 실물 사진) */
        style={
          {
            '--rackaxes-w': String(TC_PLATE_W),
            '--rackaxes-h': String(TC_PLATE_H),
          } as CSSProperties
        }
      >
        <span aria-hidden="true" className="rackaxes__head">
          {TC_AXES.head.map((label) => (
            <span
              className="rackaxes__label"
              data-axis="x"
              data-axis-cells={label.cells.join(',')}
              key={label.label}
            >
              {label.label}
            </span>
          ))}
        </span>
        <span aria-hidden="true" className="rackaxes__side">
          {TC_AXES.side.map((label) => (
            <span
              className="rackaxes__label num"
              data-axis="y"
              data-axis-cells={label.cells.join(',')}
              key={label.label}
            >
              {label.label}
            </span>
          ))}
        </span>
        <div className="rackaxes__fig">{children}</div>
      </div>
    )
  }
  const layout = rackLayoutOf(kind, definition)
  const axes =
    layout === null || layout.holes.some((hole) => hole.name === null)
      ? null
      : rackAxes(
          layout.width,
          layout.height,
          layout.holes.map((hole) => ({ name: hole.name ?? '', cx: hole.cx, cy: hole.cy })),
        )
  if (axes === null) return <>{children}</>
  const at = (value: number) => ({ '--at': String(value) }) as CSSProperties
  return (
    <div
      className="rackaxes"
      data-rack={kind}
      ref={thinOnResize}
      /*
       * 그림의 폭 ÷ 높이 — 그림 칸이 이 비율대로만 줄고 늘게(CSS 가 키를 정하고 폭은 이 비율로 · 좁으면 폭이 먼저 줄고 키가 따라온다).
       * 비율이 어긋나면 그림이 상자 안에서 위아래로 떠(meet) 왼쪽 숫자가 칸과 어긋난다
       */
      style={
        {
          '--rackaxes-w': String(layout?.width ?? 1),
          '--rackaxes-h': String(layout?.height ?? 1),
        } as CSSProperties
      }
    >
      <span aria-hidden="true" className="rackaxes__head">
        {axes.head.map((label) => (
          <span
            className={cx('rackaxes__label', axes.lettersSide && 'num')}
            key={label.label}
            style={at(label.at)}
          >
            {label.label}
          </span>
        ))}
      </span>
      <span aria-hidden="true" className="rackaxes__side">
        {axes.side.map((label) => (
          <span
            className={cx('rackaxes__label', !axes.lettersSide && 'num')}
            key={label.label}
            style={at(label.at)}
          >
            {label.label}
          </span>
        ))}
      </span>
      <div className="rackaxes__fig">
        {children}
        {axes.bands.map((band, n) => (
          <span
            aria-hidden="true"
            className={cx(
              'rackaxes__band',
              axes.lettersSide && 'rackaxes__band--y',
              /* 마지막 묶음은 판 아래 끝까지 이어진다 — 그 머리는 랙 «밖» 아래 줄에 선다(판 위 틈에 세우면 웰을 덮었다 · 2026-10-26) */
              !axes.lettersSide && n === axes.bands.length - 1 && 'rackaxes__band--foot',
            )}
            key={band.at}
            style={at(band.at)}
          >
            {band.labels.map((label) => (
              <span className="rackaxes__label" key={label.label} style={at(label.at)}>
                {label.label}
              </span>
            ))}
          </span>
        ))}
      </div>
    </div>
  )
}

/**
 * **시약 줄 목록 — 시약대별로 묶은 레시피 시약 하나당 한 줄**(eric 2026-10-02 「저렇게 숫자 말고... 실제 레시피에서 사용되는 시약으로 … 굳이 사각형
 * 아니어도 되고... 한 줄에 하나씩.. 시약 하나씩 표현하면 됨.. 시약은 있냐 없냐로만 판단하면 됨..」 → 같은 날 「시약대 별로... 리스트를 적어줘..」).
 *
 * ```
 * ┌──────────────────────────────────────────────┐
 * │ 상온 시약대 1                                 │  ← 시약대 소제목(`reagentBayName()`)
 * │ 워싱용액 DPBS              A1 ● A2 ● A3 ○ │  ← 칸마다 칸 이름 · 점(`wellPresence()` · 상태 글자는 보조기술 · 툴팁만)
 * ├──────────────────────────────────────────────┤  ← 시약대 사이 구분선
 * │ 냉장 시약대 4℃                                │  ← 시약대 시약이 전부 같은 보관 온도면 소제목에 한 번
 * │ 1차 항체 용액                           A1 ● B1 ○ │
 * │ DAPI 용액                  A3 ● B3 ▨ C3 ▨ │
 * └──────────────────────────────────────────────┘
 * ```
 *
 * 🔴 묶음 차례는 `SupplySummary.reagentBays`(레시피 `labware` 차례), 묶음 안은 채널 차례다. 줄에는 이름(`display_name` 먼저)과 칸 표지만
 *    선다 — 성분명(`name`)은 다를 때만 줄의 `title` 에 실린다(2026-10-03 줄에서 뺐다 · `ReagentRow`).
 * 🔴 보관 온도는 시약대의 시약이 전부 같으면 소제목에 한 번이다(`ReagentBay.storageCelsius`) — 줄마다의 「4℃」는 걷었다(중복).
 *    한 시약대 안에서 온도가 섞이면(표준 레시피에는 없다) 줄마다 제 온도를 적는다.
 * 🔴 판정은 `reagentPresence()` 한 곳(`ReagentSupply.presence`)이다 — 여기서 웰을 다시 세지 않는다.
 * 🔴 **색만으로 말하지 않는다** — 칸마다 점 «모양»(채움 · 빈 원 · 빗금)이 셋 다 다르고 카드 머리 범례가 글자로 풀어 준다(칸의 상태
 *    글자는 2026-10-02 에 화면에서 뺐다 · 보조기술 · 툴팁에는 남는다). 「없음」은 부족이지 실패가 아니라 위험색을 쓰지 않는다 — 굵은 칸 이름. 「미수신」을 있음 · 없음으로 접지 않는다.
 * ⚠ 종전 웰 7열 번호 격자(`ReagentGrid` · 1~14 네모)는 걷었다 — 되살리지 마라.
 */
export function ReagentList({
  summary,
  bays = summary.reagentBays,
  label = t('monitoring.supply.reagentList'),
  bayHeads = true,
}: {
  summary: SupplySummary
  /**
   * 그릴 시약대 — 기본은 전부(`summary.reagentBays`). 시약 카드의 랙 고르기(`SupplyPanel` · eric 2026-10-02 「3개 랙 중에..
   * 하나식 선택해서」)는 고른 랙 하나만 넘긴다.
   */
  bays?: readonly ReagentBay[]
  /** 목록 접근 이름 — 랙 하나만 그릴 때는 그 랙 이름 */
  label?: string
  /**
   * 시약대 소제목(이름 · 보관 온도)을 세우나. 랙 고르기에서는 고른 랙 이름이 선택기에 서고 보관 온도도 선택지에 붙어 소제목이
   * 같은 말을 두 번 한다 — 끈다. ⚠ 되돌릴 때는 `SupplyPanel` 이 이 값을 안 넘기면 된다(기본은 세운다).
   */
  bayHeads?: boolean
}) {
  return (
    <ul aria-label={label} className="supreagent">
      {bays.map((bay) => {
        const name = reagentBayName(bay)
        return (
          <li className="supreagent__bay" data-bay={bay.labwareId} key={bay.labwareId ?? ''}>
            {bayHeads ? (
              <p className="supreagent__bayhead">
                <span className="supreagent__bayname">{name}</span>
                {bay.storageCelsius === undefined ? null : <Storage celsius={bay.storageCelsius} />}
              </p>
            ) : null}
            {/* 소제목이 없으면 묶음 이름도 붙이지 않는다 — 바깥 목록 이름(고른 랙)과 다른 이름(시약대)이 두 번 읽히지 않게 */}
            <ul aria-label={bayHeads ? name : undefined} className="supreagent__rows">
              {bay.reagents.map((reagent) => (
                <ReagentRow
                  key={reagent.key}
                  reagent={reagent}
                  showStorage={bay.storageCelsius === undefined}
                />
              ))}
            </ul>
          </li>
        )
      })}
    </ul>
  )
}

/** 보관 온도 — 눈에는 「4℃」, 보조기술은 「보관 4℃」. 시약 카드의 랙 선택지(`SupplyPanel`)도 같은 표지를 붙인다 */
export function Storage({ celsius }: { celsius: number }) {
  return (
    <span className="supreagent__temp num">
      <span aria-hidden="true">{t('monitoring.supply.celsius', { celsius: String(celsius) })}</span>
      <span className="a-sr">{t('monitoring.supply.storage', { celsius: String(celsius) })}</span>
    </span>
  )
}

/** 있다 · 없다 · 미수신 글자 — 칸 표지의 보조기술 글자 · 툴팁이 쓴다(화면에는 안 보인다) */
function presenceText(presence: ReagentPresence): string {
  return presence === 'unreceived'
    ? t('monitoring.supply.unreceived')
    : t(`monitoring.supply.presence.${presence}`)
}

/**
 * 시약 한 줄 — 이름 · 칸마다 [칸 이름 · 점](eric 2026-10-02 「A1, A2, A3 이거.. 각각 있음 없음 나오게 표현해줘」 →
 * 같은 날 머리 범례가 서며 「중복된 건 빼야지」 — 있음/없음/미수신 글자는 보조기술 · 툴팁만). 칸 판정은 `wellPresence()` 한 곳이다.
 * 🔴 줄 끝의 «시약 전체» 판정 글자(종전 `.supreagent__state` · ▲ 없음)는 걷었다 — 칸마다 글자가 서서 같은 말이 두 번이 된다.
 *    시약 전체 판정(`ReagentSupply.presence`)은 줄의 `data-presence` 와 카드 머리 수치(시약 단위)가 그대로 쓴다.
 * 🔴 색만으로 말하지 않는다 — 점 모양(채움 · 빈 원 · 빗금)이 셋 다 다르고 머리 범례가 글자로 푼다. 보조기술은 칸마다 「A1 있음」. 없음은 굵게.
 */
function ReagentRow({ reagent, showStorage }: { reagent: ReagentSupply; showStorage: boolean }) {
  const substance = reagent.substance === reagent.name ? undefined : reagent.substance
  const storage =
    reagent.storageCelsius === undefined
      ? undefined
      : t('monitoring.supply.storage', { celsius: String(reagent.storageCelsius) })
  /* 칸마다(레시피 `volumeByWell` 의 웰 차례) — 칸이 없는 시약(레시피에 웰 없음)은 시약 판정 하나를 이름 없이 적는다 */
  const wells: { key: string; name: string | undefined; presence: ReagentPresence }[] =
    reagent.wells.length === 0
      ? [{ key: '', name: undefined, presence: reagent.presence }]
      : reagent.wells.map((well) => ({
          key: well.well,
          name: well.well,
          presence: wellPresence(well),
        }))
  const wellText = (well: (typeof wells)[number]) =>
    [well.name, presenceText(well.presence)].filter(Boolean).join(' ')
  return (
    <li
      className="supreagent__row"
      data-channel={String(reagent.channel)}
      data-presence={reagent.presence}
      title={[reagent.name, substance, wells.map(wellText).join(' · '), storage]
        .filter(Boolean)
        .join(' · ')}
    >
      {/*
       * 성분명(`substance` · 레시피 `liquids[].description` · 「>1% 1차 항체, PBS-T」)은 이름 아래 작은 글자 한 줄(eric 2026-10-18 「상세 내용도 적어줘」 —
       * 2026-10-03 팀장 결정으로 `title` 에만 두었던 것을 되살렸다). 이름과 같으면 안 적는다
       */}
      <span className="supreagent__label">
        <span className="supreagent__name">{reagent.name}</span>
        {substance === undefined ? null : (
          <span className="supreagent__substance">{substance}</span>
        )}
      </span>
      <span className="supreagent__where">
        <span className="supreagent__wells">
          {wells.map((well) => (
            <span
              className="supreagent__well"
              data-presence={well.presence}
              data-well={well.name}
              key={well.key}
              title={wellText(well)}
            >
              {/* 칸 이름 먼저 · 점이 뒤(eric 2026-10-02 「A1 O 이렇게 해줘... 지금 거꾸로 되어 있어..」). 머리 범례는 관례대로 「점 + 뜻」이다 */}
              {well.name === undefined ? null : (
                <span aria-hidden="true" className="supreagent__wellname num">
                  {well.name}
                </span>
              )}
              <i aria-hidden="true" className="supreagent__dot" />
              {/*
               * 상태 글자는 화면에서 뺐다(eric 2026-10-02 「범례를 타이틀에 붙였잖아. 그러면.. 밑에.. 중복된 건 빼야지」) — 점 모양 셋과
               * 머리 범례(`ReagentKeys`)가 말한다. 보조기술은 「A1 있음」을 그대로 읽는다(`title` 도 같다). 되살리지 마라.
               */}
              <span className="supreagent__welltext a-sr">{wellText(well)}</span>
            </span>
          ))}
        </span>
        {showStorage && reagent.storageCelsius !== undefined ? (
          <Storage celsius={reagent.storageCelsius} />
        ) : null}
      </span>
    </li>
  )
}
